import { Box } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { AiLoading } from 'src/components/ai/AiLoading';
import { useLocales } from 'src/locales';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import {
  AiSendButton,
  CoperaAiButton,
  CoperaAiButtonDisabled,
} from 'src/sections/@dashboard/chat/ChatVoiceChannel/VoiceChannelControls/Buttons/AskToAiButton/index';
import { useSocketIO } from 'src/socket';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record';
import { AiDeleteButton } from './AiDeleteButton';

type Props = {
  onSend: (file: File) => void;
  isFromNav?: boolean;
};

export const AskToAiButton = ({ onSend, isFromNav }: Props) => {
  const theme = useTheme();
  const { io } = useSocketIO();
  const { translate } = useLocales();

  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer>(null);
  const recordRef = useRef<RecordPlugin>(null);

  const {
    activeVoiceChannel,
    myMeetId,
    channelLockedBy,
    setChannelLockedBy,
    aiActive,
    aiStatus,
    setAiStatus,
  } = useLivekitRTCState();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!containerRef.current) return () => {};

    const wave = WaveSurfer.create({
      container: containerRef.current,
      waveColor: theme.palette.primary.main,
      progressColor: theme.palette.primary.main,
      // responsive: true,
      height: 24,
      barHeight: 8,
      barGap: 4,
      barWidth: 2,
      barRadius: 1,
      cursorWidth: 0,
      interact: false,
    });

    const record = wave.registerPlugin(
      RecordPlugin.create({ scrollingWaveform: true }),
    );

    // @ts-ignore
    wavesurferRef.current = wave;
    // @ts-ignore
    recordRef.current = record;

    return () => {
      wave?.destroy();
      // @ts-ignore
      wavesurferRef.current = null;
      // @ts-ignore
      recordRef.current = null;
    };
  }, [containerRef, theme.palette.grey, theme.palette.secondary.main]);

  const sendRecording = async () => {
    if (!recordRef.current) return;

    recordRef.current.once('record-end', async (blob) => {
      const file = new File([blob], `audio.mp3`, { type: 'audio/mpeg' });
      onSend(file);
      setAiStatus('loading');
    });

    // wavesurferRef.current?.stop();
    recordRef.current.stopRecording();
  };

  const startRecording = () => {
    if (!recordRef.current || !myMeetId) return;

    recordRef.current.startRecording().then(() => {
      io.emit(`/voice-channel/lock-ai`, {
        channelId: activeVoiceChannel.channel._id,
        meetId: myMeetId,
      });
      setChannelLockedBy(myMeetId);
      setAiStatus('recording');
    });
  };

  const deleteRecord = () => {
    if (!recordRef.current) return;
    recordRef.current.stopRecording();
    io.emit(`/voice-channel/unlock-ai`, {
      channelId: activeVoiceChannel.channel._id,
    });
    setChannelLockedBy(null);
    setAiStatus(null);
  };

  return (
    <>
      {(!aiActive || (channelLockedBy && channelLockedBy !== myMeetId)) && (
        <CoperaAiButtonDisabled
          tooltip={
            !aiActive
              ? translate('communicate.rtc_controls.copera_not_active')
              : translate('communicate.rtc_controls.copera_locked')
          }
          isFromNav={isFromNav}
        />
      )}
      {aiActive && channelLockedBy === myMeetId && (
        <>
          {aiStatus === 'recording' && (
            <>
              <AiSendButton isFromNav={isFromNav} onClick={sendRecording} />
              <AiDeleteButton isFromNav={isFromNav} onClick={deleteRecord} />
            </>
          )}
          {aiStatus === 'loading' && (
            <StyledAiLoading className={classNames({ isFromNav })} />
          )}
          {aiStatus === 'speaking' && (
            <CoperaAiButtonDisabled
              isFromNav={isFromNav}
              tooltip={translate('communicate.rtc_controls.copera_is_speaking')}
            />
          )}
        </>
      )}
      {aiActive && !channelLockedBy && (
        <CoperaAiButton isFromNav={isFromNav} onClick={startRecording} />
      )}

      <Box
        ref={containerRef}
        sx={{ position: 'absolute', visibility: 'hidden', width: 0, height: 0 }}
      />
    </>
  );
};

const StyledAiLoading = styled(AiLoading)(({ theme }) => ({
  width: 36,
  height: 36,
  [theme.breakpoints.up('md')]: {
    width: 40,
    height: 40,
  },
  '&.isFromNav': {
    width: '100%',
    height: theme.spacing(4),
    marginBottom: theme.spacing(-1),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
